import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

interface ButtonColor {
  button_id: string;
  bg_hex?: string;
  accent_hex?: string;
}

interface ButtonColorsPair {
  bg: string;
  accent: string;
}

interface ButtonColorsContextType {
  getButtonColors: (buttonId: string) => ButtonColorsPair | null;
  refreshColors: () => Promise<void>;
  isLoading: boolean;
}

const DEFAULT_PASTEL: Record<string, ButtonColorsPair> = {
  bons_de_commande: { bg: '#E8F0FE', accent: '#2F6FED' },
  bons_de_commande_2: { bg: '#E8F0FE', accent: '#2F6FED' },
  factures: { bg: '#FDECEC', accent: '#D64545' },
  liste_acomptes: { bg: '#F3F4F6', accent: '#6B7280' },
  notes_credit: { bg: '#FCE8E8', accent: '#B91C1C' },
  clients: { bg: '#E8F5EC', accent: '#2E8B57' },
  articles: { bg: '#F2EAFE', accent: '#7C3AED' },
  recherche: { bg: '#ECEFF1', accent: '#374151' },
  services_apres_vente: { bg: '#FFF1E6', accent: '#EA7C2D' },
  livraisons_services: { bg: '#E6F4F3', accent: '#0F766E' },
};

const ButtonColorsContext = createContext<ButtonColorsContextType | undefined>(undefined);

export const ButtonColorsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [buttonColors, setButtonColors] = useState<Record<string, ButtonColor>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadButtonColors();
  }, []);

  const loadButtonColors = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('button_colors')
        .select('button_id, bg_hex, accent_hex');

      if (error) {
        // Table peut ne pas avoir encore bg_hex/accent_hex
      } else if (data) {
        const colorsMap: Record<string, ButtonColor> = {};
        data.forEach((c: ButtonColor) => {
          colorsMap[c.button_id] = c;
        });
        setButtonColors(colorsMap);
      }
    } catch {
      // Ignorer
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonColors = (buttonId: string): ButtonColorsPair | null => {
    const saved = buttonColors[buttonId];
    const def = DEFAULT_PASTEL[buttonId];

    if (saved?.bg_hex && saved?.accent_hex) {
      return { bg: saved.bg_hex, accent: saved.accent_hex };
    }
    return def ?? null;
  };

  const refreshColors = async () => {
    await loadButtonColors();
  };

  return (
    <ButtonColorsContext.Provider value={{ getButtonColors, refreshColors, isLoading }}>
      {children}
    </ButtonColorsContext.Provider>
  );
};

export const useButtonColors = () => {
  const context = useContext(ButtonColorsContext);
  if (context === undefined) {
    throw new Error('useButtonColors must be used within a ButtonColorsProvider');
  }
  return context;
};
